import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { Calculator, FileText, Pencil, Settings, Eye, Globe, Zap, Shield, Download, FileSpreadsheet, Layers, TrendingUp, FlaskConical, BarChart2 } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

const BASE = import.meta.env.BASE_URL

const featurePhotos = [
  `${BASE}screenshots/calculator.png?v=2`,
  `${BASE}screenshots/step-import.png?v=2`,
  `${BASE}screenshots/dxf-editor.png?v=2`,
  `${BASE}screenshots/nesting.png?v=2`,
  `${BASE}screenshots/offers.png?v=2`,
  `${BASE}screenshots/part-detail.png?v=2`,
]

const featureIcons = [
  <Calculator className="h-12 w-12" />,
  <FileText className="h-12 w-12" />,
  <Pencil className="h-12 w-12" />,
  <Settings className="h-12 w-12" />,
  <Eye className="h-12 w-12" />,
  <Globe className="h-12 w-12" />,
]

const additionalIcons = [
  <Zap className="h-6 w-6" />,
  <Shield className="h-6 w-6" />,
  <Download className="h-6 w-6" />,
  <FileSpreadsheet className="h-6 w-6" />,
  <Layers className="h-6 w-6" />,
  <TrendingUp className="h-6 w-6" />,
]

const translations = {
  el: {
    hero: {
      title: 'Ισχυρές Λειτουργίες για Επαγγελματικά Αποτελέσματα',
      subtitle: 'Όλα όσα χρειάζεστε για να βελτιστοποιήσετε τη διαδικασία προσφορών, από εισαγωγή CAD έως επαγγελματικές αναφορές Excel.',
    },
    features: [
      {
        title: 'Υπολογιστής Κόστους',
        description: 'Αυτόματη σάρωση φακέλων με μεταδεδομένα εξαρτημάτων και δημιουργία ακριβών εκτιμήσεων κόστους για ελάσματα (laser/ψαλίδι + κάμψη) και εξαρτήματα CNC router.',
        features: ['Αυτόματη αναγνώριση υλικού', 'Υποστήριξη πολλαπλών μεθόδων κοπής', 'Διαχείριση ποσοτήτων ανά εξάρτημα', 'Υπολογισμοί κόστους σε πραγματικό χρόνο', 'Ξεχωριστές προσφορές για ελάσματα και router'],
        learnMore: [
          { label: 'Κοστολόγηση παραγωγής', path: '/production-costing' },
          { label: 'Κοστολόγηση κάμψης', path: '/bend-costing' },
          { label: 'Κοστολόγηση laser', path: '/laser-costing' },
        ],
      },
      {
        title: 'Εισαγωγή DXF & STEP',
        description: 'Εισαγωγή αρχείων CAD και αυτόματη εξαγωγή γεωμετρικών πληροφοριών, συμπεριλαμβανομένων διαστάσεων, οπών, κάμψεων και περιμέτρων.',
        features: ['Υποστήριξη DXF με χειροκίνητη εισαγωγή πάχους', 'Υποστήριξη STEP με αυτόματη ανίχνευση πάχους', 'Αυτόματη ανάλυση γεωμετρίας', 'Επιλογή υλικού κατά την εισαγωγή', 'Δυνατότητα μαζικής επεξεργασίας'],
        learnMore: [
          { label: 'Εισαγωγή DXF & STEP', path: '/dxf-step-import' },
          { label: 'Plugin SolidWorks', path: '/solidworks-plugin' },
          { label: 'Plugin Inventor', path: '/inventor-plugin' },
        ],
      },
      {
        title: 'Επεξεργαστής Γεωμετρίας DXF',
        description: 'Οπτική ταξινόμηση περιγραμμάτων, χαρακτηριστικών και γραμμών κάμψης απευθείας στη γεωμετρία DXF. Αυτόματη ανίχνευση οπών, κάμψεων και μετρήσεων περιμέτρου.',
        features: ['Ταξινόμηση εξωτερικού περιγράμματος με κλικ', 'Ανίχνευση χαρακτηριστικών και γραμμών κάμψης', 'Αυτόματη ανίχνευση οπών/κύκλων', 'Πίνακας ανιχνευμένων μετρήσεων', 'Υποστήριξη καμπύλων ακμών και λοξοτομών'],
        learnMore: [{ label: 'Εισαγωγή DXF & STEP', path: '/dxf-step-import' }],
      },
      {
        title: 'Επεξεργαστής Παραμέτρων',
        description: 'Προσαρμόστε όλες τις ρυθμίσεις μηχανημάτων, κόστη υλικών και επιχειρηματικές παραμέτρους για το εργαστήριό σας. Περιλαμβάνει οπτικοποίηση nesting.',
        features: ['Ρυθμίσεις laser, ψαλιδιού, κάμψης, router', 'Πυκνότητα και κόστος υλικών', 'Ταρίφες ηλεκτρισμού και εργατικών', 'Ρύθμιση περιθωρίου κέρδους', 'Γρήγορη επεξεργασία και λειτουργία JSON'],
        learnMore: [
          { label: 'Κοστολόγηση παραγωγής', path: '/production-costing' },
          { label: 'Κοστολόγηση σωλήνα laser', path: '/tube-laser-costing' },
        ],
      },
      {
        title: 'Διαχείριση Προσφορών',
        description: 'Προβολή, αναζήτηση και διαχείριση όλων των δημιουργημένων προσφορών με ισχυρά φίλτρα και δυνατότητες επανυπολογισμού.',
        features: ['Φιλτράρισμα κατά εύρος ημερομηνιών', 'Φιλτράρισμα κατά εύρος τιμών', 'Φιλτράρισμα βάσει υλικού', 'Επανυπολογισμός με διαφορετικά markup', 'Γρήγορη πρόσβαση σε αρχεία Excel'],
        learnMore: [{ label: 'Λογισμικό προσφορών', path: '/quotation-software' }],
      },
      {
        title: 'Λεπτομέρειες & Επιθεώρηση Εξαρτήματος',
        description: 'Επιθεωρήστε κάθε εισαγόμενο εξάρτημα — δείτε το 3D μοντέλο, υλικό, πάχος, βάρος, οπές, κάμψεις και ποσότητα στο assembly.',
        features: ['Διαδραστική 3D προεπισκόπηση', 'Πλήρης ανάλυση γεωμετρίας', 'Δεδομένα βάρους και περιμέτρου', 'Παρακολούθηση ποσότητας assembly', 'Ετικέτα ταξινόμησης Laser Cut / Bent'],
      },
    ],
    additional: [
      { title: 'Εξαιρετική Ταχύτητα', description: 'Δημιουργήστε προσφορές σε δευτερόλεπτα με βελτιστοποιημένους υπολογισμούς' },
      { title: 'Ασφαλές & Ιδιωτικό', description: 'Desktop εφαρμογή — τα δεδομένα σας δεν φεύγουν ποτέ από τον υπολογιστή σας' },
      { title: 'Εύκολη Εγκατάσταση', description: 'Ένα εκτελέσιμο αρχείο εγκατάστασης, χωρίς πολύπλοκη ρύθμιση' },
      { title: 'Αναφορές Excel', description: 'Επαγγελματικές προσφορές Excel με λεπτομερή ανάλυση' },
      { title: 'Πολλαπλά Υλικά', description: 'Χάλυβας, αλουμίνιο, πολυκαρβονικό, MDF και άλλα' },
      { title: 'Βελτιστοποίηση Κόστους', description: 'Αυτόματη εύρεση καλύτερης μεθόδου κατασκευής' },
    ],
    additionalTitle: 'Και Πολλές Άλλες Λειτουργίες',
    additionalSubtitle: 'Το Workshop Assistant είναι γεμάτο λειτουργίες σχεδιασμένες να κάνουν τη δουλειά σας ευκολότερη.',
    cta: {
      title: 'Δοκιμάστε Όλες τις Λειτουργίες Σήμερα',
      subtitle: 'Κατεβάστε τη δωρεάν δοκιμή και εξερευνήστε όλες τις λειτουργίες για 5 ημέρες δωρεάν.',
      btn: 'Δωρεάν Δοκιμή',
    },
  },
  en: {
    hero: {
      title: 'Powerful Features for Professional Results',
      subtitle: 'Everything you need to streamline your quoting process, from CAD import to professional Excel reports.',
    },
    features: [
      {
        title: 'Cost Calculator',
        description: 'Automatically scan folders containing part metadata and generate accurate cost estimates for sheet metal (laser/guillotine + bending) and CNC router parts.',
        features: ['Auto-detection of material type', 'Support for multiple cutting methods', 'Quantity management per part', 'Real-time cost calculations', 'Separate offers for sheet metal and router'],
        learnMore: [
          { label: 'Production costing', path: '/production-costing' },
          { label: 'Bend costing', path: '/bend-costing' },
          { label: 'Laser costing', path: '/laser-costing' },
        ],
      },
      {
        title: 'DXF & STEP Import',
        description: 'Import CAD files and automatically extract geometric information including dimensions, holes, bends, and perimeters.',
        features: ['DXF file support with manual thickness input', 'STEP file support with auto-detected thickness', 'Automatic geometry analysis', 'Material selection during import', 'Batch processing capability'],
        learnMore: [
          { label: 'DXF & STEP import', path: '/dxf-step-import' },
          { label: 'SolidWorks plugin', path: '/solidworks-plugin' },
          { label: 'Inventor plugin', path: '/inventor-plugin' },
        ],
      },
      {
        title: 'DXF Geometry Editor',
        description: 'Visually classify contours, features, and bend lines directly on the DXF geometry. Auto-detects holes, bends, and perimeter metrics.',
        features: ['Click-to-classify outer contour segments', 'Feature and bend line detection', 'Hole/Circle auto-detection', 'Detected metrics panel (flat size, holes, bends)', 'Curved edges and chamfer support'],
        learnMore: [{ label: 'DXF & STEP import', path: '/dxf-step-import' }],
      },
      {
        title: 'Parameters Editor',
        description: 'Customize all machine settings, material costs, and business parameters to match your workshop. Includes nesting visualization for optimal sheet utilization.',
        features: ['Laser, guillotine, bending, router settings', 'Material density and costs', 'Electricity and workforce rates', 'Profit markup configuration', 'Quick edit and JSON modes'],
        learnMore: [
          { label: 'Production costing', path: '/production-costing' },
          { label: 'Tube laser costing', path: '/tube-laser-costing' },
        ],
      },
      {
        title: 'Offers Management',
        description: 'View, search, and manage all generated offers with powerful filtering and recalculation features.',
        features: ['Date range filtering', 'Price range filtering', 'Material-based filtering', 'Offer recalculation with different markups', 'Quick access to Excel files'],
        learnMore: [{ label: 'Quotation software', path: '/quotation-software' }],
      },
      {
        title: 'Part Detail & Inspection',
        description: 'Inspect every imported part in detail — view the 3D model, material, thickness, weight, holes, bends, and quantity in assembly.',
        features: ['Interactive 3D part preview', 'Full geometry breakdown', 'Weight and perimeter data', 'Assembly quantity tracking', 'Laser Cut / Bent classification badge'],
      },
    ],
    additional: [
      { title: 'Lightning Fast', description: 'Generate quotes in seconds with optimized calculations' },
      { title: 'Secure & Private', description: 'Desktop app - your data never leaves your computer' },
      { title: 'Easy Installation', description: 'Single executable installer, no complex setup' },
      { title: 'Excel Reports', description: 'Professional Excel offers with detailed breakdowns' },
      { title: 'Multiple Materials', description: 'Steel, aluminum, polycarbonate, MDF, and more' },
      { title: 'Cost Optimization', description: 'Find the best manufacturing method automatically' },
    ],
    additionalTitle: 'And Many More Features',
    additionalSubtitle: 'Workshop Assistant is packed with features designed to make your work easier.',
    cta: {
      title: 'Experience All Features Today',
      subtitle: 'Download the free trial and explore all features free for 5 days.',
      btn: 'Download Free Trial',
    },
  },
}

const Features = () => {
  const { lang } = useLanguage()
  const t = translations[lang]

  return (
    <div className="overflow-hidden">
      <SEO
        title="Features — Calculator, DXF Import, Nesting & Reports"
        description="Explore Workshop Assistant's features: cost calculator, DXF/STEP import, nesting, offer generation, and Excel reporting for sheet metal fabrication."
        path="/features"
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90">{t.hero.subtitle}</motion.p>
          </div>
        </div>
      </section>

      {/* Main Features */}
      <section className="section-container bg-white">
        <div className="space-y-24">
          {t.features.map((feature, index) => (
            <motion.div key={index} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`}>
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-100 text-primary-600 rounded-2xl mb-6">
                  {featureIcons[index]}
                </div>
                <h2 className="text-3xl font-bold mb-4">{feature.title}</h2>
                <p className="text-lg text-gray-600 mb-6">{feature.description}</p>
                <ul className="space-y-3">
                  {feature.features.map((item, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                        <svg className="w-4 h-4 text-green-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor"><path d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                {feature.learnMore && feature.learnMore.length > 0 && (
                  <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6">
                    {feature.learnMore.map((link, i) => (
                      <Link key={i} to={link.path} className="text-primary-600 font-medium hover:text-primary-800 transition-colors">
                        {link.label} →
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
                  <img src={featurePhotos[index]} alt={feature.title} className="w-full h-auto object-cover" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Additional Features */}
      <section className="section-container bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t.additionalTitle}</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t.additionalSubtitle}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.additional.map((feature, index) => (
            <motion.div key={index} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} className="card text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-600 rounded-xl mb-4">{additionalIcons[index]}</div>
              <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-container bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.cta.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.cta.subtitle}</p>
          <Link to="/download" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <Download className="h-5 w-5" /><span>{t.cta.btn}</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default Features
